import React, { use, useEffect } from 'react';
import { Lock, Pencil } from 'lucide-react';
import { GenderDTO, MemberDTO } from '../../services/apiClient';
import Badge from '../common/Badge';
import Button from '../common/Button';
import { getBadgeColors } from '../../utils/displayUtils';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { useNavigate } from 'react-router-dom';
import { useImpersonation } from '../../hooks/useImpersonation';
import { useMemberApi } from '../../hooks/useMemberApi';

interface MemberQuickDetailsProps {
    memberId: string;
    onClose: () => void;
}

const MemberQuickDetails: React.FC<MemberQuickDetailsProps> = ({
    memberId,
    onClose,
}) => {
    const navigate = useNavigate();
    const { impersonate } = useImpersonation();

    const [member, setMember] = React.useState<MemberDTO | null>(null);

    const {getById} = useMemberApi();

    useEffect(() => {
        const fetchMember = async () => {
            try {
                const response = await getById(memberId);
                setMember(response?.data ?? null);
            } catch (error) {
                console.error('Error fetching member:', error);
            }
        };

        fetchMember();
    }, [memberId]);

    const getStatusBadge = (title?: string, colour?: string) => {
        if (!title) return <span>-</span>;
        if (!colour) return <span>{title}</span>;
        const { bg, text } = getBadgeColors(colour, 0.18, 0.4);
        return <Badge label={title} variant="custom" customBgColor={bg} customTextColor={text} />;
    };

    const rows: { label: string; value: React.ReactNode }[] = [
        { label: 'Initials', value: member?.initials || '-' },
        { label: 'Name', value: member?.firstName || '-' },
        { label: 'Surname', value: member?.lastName || '-' },
        { label: 'ID Number', value: member?.idPassportNumber || '-' },
        { label: 'FCA Status', value: member?.memberCategory?.titleShort || '-' },
        { label: 'Membership', value: member?.memberType?.title || '-' },
        {
            label: 'Status',
            value: getStatusBadge(member?.memberStatus?.title, member?.memberStatus?.colourGrading?.colour),
        },
        {
            label: 'Disciplinary',
            value: getStatusBadge(member?.memberDisciplinaryStatus?.title, member?.memberDisciplinaryStatus?.colourGrading?.colour),
        },
        { label: 'Gender', value: member?.gender?.title || '-' },
        { label: 'Termination', value: getStatusBadge(member?.memberTerminationStatus?.title, member?.memberTerminationStatus?.colourGrading?.colour) },
        { label: 'Language', value: member?.language?.title || member?.languageOther || '-' },
        {
            label: 'Cellphone',
            value: member?.phone
                ? <a href={`tel:${member.phone}`} className="text-blue-500 hover:underline">{member.phone}</a>
                : '-',
        },
        {
            label: 'Email',
            value: member?.email
                ? <a href={`mailto:${member.email}`} className="text-blue-500 hover:underline">{member.email}</a>
                : '-',
        },
        { label: 'Courier Address', value: member?.courierAddress || '-' },
        { label: 'Physical Address', value: member?.physicalAddress || '-' },
        { label: 'Date Enrolled', value: member?.dateEnrolled ? convertToUserTime(member.dateEnrolled.toString()) : '-' },
        { label: 'Date Payment', value: member?.datePaid ? convertToUserTime(member.datePaid.toString()) : '-' },
        { label: 'Paid-up date', value: member?.paidUpTo ? convertToUserTime(member.paidUpTo.toString()) : '-' },
        { label: 'Date Certificate Hunting', value: member?.dateCertificateHunter ? convertToUserTime(member.dateCertificateHunter.toString()) : '-' },
        { label: 'Date Certificate Sport-Shooting', value: member?.dateCertificateSport ? convertToUserTime(member.dateCertificateSport.toString()) : '-' },
    ];

    return (
        <div className="flex flex-col gap-4">
            <table className="w-full text-sm">
                <tbody>
                    {rows.map(({ label, value }) => (
                        <tr key={label} className="border-b border-gray-100 dark:border-gray-700">
                            <td className="py-2 pr-4 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap w-1/3">
                                {label}:
                            </td>
                            <td className="py-2 text-gray-900 dark:text-gray-100">
                                {value}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="flex flex-wrap gap-2 mt-2">
                <Button onClick={() => navigate(`/admin/members/${member?.id}`)} icon={Pencil} className="bg-amber-500 hover:bg-amber-600 text-white border-0">
                    Edit member
                </Button>
                <Button onClick={() => impersonate(member?.id, member?.memberStatusId)} variant="secondary" icon={Lock}>
                    Log in as
                </Button>
                <Button onClick={onClose} variant="secondary">
                    Close
                </Button>
            </div>
        </div>
    );
};

export default MemberQuickDetails;